import React, { useState, useEffect, useRef } from 'react'
import styles from './travelDealsCarousel.module.css'
import { Skeleton } from '@vernost_dev/vms-react-components/dist/Skeleton'

export default function TravelDealsCarousel({ tourCarouselData = [], isMobile, loading }) {
  const [current, setCurrent] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  const total = tourCarouselData.length || 7
  const transitionDuration = 400 // match with CSS animation duration (ms)
  const autoRotateIntervalMs = 5000 // interval in ms

  // keep a ref to the interval id so we can clear it safely
  const intervalRef = useRef(null)

  const nextSlide = () => {
    if (isTransitioning || total === 0) return
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % total)
      setIsTransitioning(false)
    }, transitionDuration)
  }

  const prevSlide = () => {
    if (isTransitioning || total === 0) return
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrent((prev) => (prev - 1 + total) % total)
      setIsTransitioning(false)
    }, transitionDuration)
  }

  const handleCardClick = (index) => {
    if (isTransitioning || loading) return
    setCurrent(index)
  }

  const getPositionClass = (index) => {
    const relative = (index - current + total) % total
    if (relative === 0) return styles.active
    if (relative === 1) return styles.next
    if (relative === total - 1) return styles.prev
    if (relative === 2) return styles.farNext
    if (relative === total - 2) return styles.farPrev
    if (relative === 3) return styles.extremeNext
    if (relative === total - 3) return styles.extremePrev
    return ''
  }

  const handleImageError = (e) => {
    e.target.onerror = null
    e.target.src = 'images/userplaceholder.svg'
  }

  // --- Auto-rotate effect (stable + respects pause/visibility/loading/total) ---
  useEffect(() => {
    // helper to clear current interval
    const clearCurrentInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    // if we shouldn't auto-rotate, ensure there's no interval running
    if (loading || total === 0 || isPaused) {
      clearCurrentInterval()
      return
    }

    // start interval if none exists
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        // we directly update current here to avoid coupling with nextSlide's transition lock
        setCurrent((prev) => (total ? (prev + 1) % total : prev))
      }, autoRotateIntervalMs)
    }

    // cleanup function
    return () => {
      clearCurrentInterval()
    }
    // Note: intentionally does NOT depend on `current` to avoid recreating on every slide change
  }, [loading, total, isPaused]) // re-run only when these change

  // --- Pause when page/tab hidden (visibilitychange) ---
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        setIsPaused(true)
      } else {
        setIsPaused(false)
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])

  // --- Ensure interval cleared on unmount ---
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // --- UI event handlers to set pause state ---
  const onMouseEnter = () => setIsPaused(true)
  const onMouseLeave = () => setIsPaused(false)

  // For mobile: pause while user touches/presses (swipe or long press)
  //  const onTouchStart = () => setIsPaused(true)
  // const onTouchEnd = () => {
  //   setTimeout(() => setIsPaused(false), 3000) // restart after 3 seconds
  // }

  // --- Rendering (kept same as your original, with container handlers added) ---
  if (isMobile) {
    return (
      <div
        className={styles.mobileSlider}
      // onTouchStart={onTouchStart}
      // onTouchEnd={onTouchEnd}
      // no hover on mobile but keeping handlers safe
      >
        {tourCarouselData.map((tour, idx) => (
          <div key={tour?.customer_id || idx} className={styles.mobileCard}>
            {loading ? (
              <div className={styles.skeleton_main_mobile}>
                <Skeleton variant="rectangular" height={160} width="100%" />
                <div className={styles.mobilecontent}>
                  <div className={styles.skeletonRoundedmobile}>
                    <Skeleton variant="circular" height={75} width={75} />
                  </div>
                  <Skeleton height={20} width="80%" style={{ marginTop: '5px' }} />
                  <Skeleton height={15} width="60%" style={{ marginTop: '5px' }} />
                </div>
              </div>
            ) : (
              <>
                <img
                  src={tour.background_image ? `/shared/api/media/${tour.background_image}` : 'images/tourbg.png'}
                  alt={tour.latest_order_name}
                  className={styles.tourMasterBg}
                />
                <div
                  className={styles.mobileOverlay}
                  onClick={() => (window.location.href = `holiday/tourmasterdetail?id=${tour.customer_id}`)}
                >
                  <div className={styles.mobileHeader}>
                    <div className={styles.guide_user_wrapper}>
                      <img
                        src={tour.profile_image ? `/shared/api/media/${tour.profile_image}` : 'images/userplaceholder.svg'}
                        alt={tour.customer_name}
                        className={styles.mobileProfile}
                        onError={handleImageError}
                      />
                      <div className={styles.guide_verified_icon}>
                        <img src="images/Profile_verified_mark.svg" alt="verified" />
                      </div>
                    </div>
                    <span className={styles.mobileRating}>
                      <img src="images/star_icon.svg" alt="rating" className={styles.starIcon} />
                      {tour.rating}
                    </span>
                  </div>
                  <p className={styles.mobileTitle}>
                    Join {tour.customer_name} <br />
                    on a tour to {tour.latest_order_name}
                  </p>
                  <p className={styles.mobileInfo}>
                    Tour Date:{' '}
                    {new Date(tour.latest_order_date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      className={styles.carousel}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {!loading && tourCarouselData?.length > 1 && (
        <button
          className={`${styles.navBtn} ${styles.left}`}
          onClick={prevSlide}
          disabled={isTransitioning}
        >
          <img src="images/arrow_left.svg" alt="Previous" />
        </button>
      )}

      {loading ? (
        <div className={styles.slides}>
          {[...Array(7)].map((_, idx) => (
            <div
              key={idx}
              className={`${styles.card} ${getPositionClass(idx)}`} // This gives the overlap/stack effect
            >
              <div className={styles.skeleton_main}>
                <Skeleton variant="rectangular" height={370} width={600} />
              </div>
              <div className={styles.carousel_content}>
                <div className={styles.skeletonRounded}>
                  <Skeleton variant="circular" height={217} width={217} />
                </div>
                <div className={styles.star_info}>
                  <Skeleton height={15} width={20} />
                  <Skeleton height={15} width={80} />
                  <Skeleton height={15} width={30} />
                </div>
                <div className={styles.skeleton_flex}>
                  <div className={styles.skeleton_text}>
                    <Skeleton height={20} width={250} />
                    <Skeleton height={20} width={200} />
                    <Skeleton height={20} width={100} />
                  </div>
                  <div className={styles.skeleton_btn}>
                    <Skeleton height={25} width={120} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.slides}>
          {tourCarouselData.map((tour, idx) => (
            <div
              key={tour?.customer_id || idx}
              className={`${styles.card} ${getPositionClass(idx)}`}
              onClick={() => handleCardClick(idx)}
            >
              <>
                <img
                  src={tour.background_image ? `/shared/api/media/${tour.background_image}` : 'images/tourbg.png'}
                  alt={tour.latest_order_name}
                  className={styles.tourMasterBg}
                />

                {getPositionClass(idx) === styles.active && (
                  <div className={styles.overlay}>
                    <div className={styles.guideHeader}>
                      <div className={styles.guide_user_wrapper}>
                        <img
                          src={
                            tour.profile_image
                              ? `/shared/api/media/${tour.profile_image}`
                              : 'images/userplaceholder.svg'
                          }
                          alt={tour.customer_name}
                          className={styles.profileLarge}
                          onError={handleImageError}
                        />
                        <div className={styles.guide_verified_icon}>
                          <img src="images/Profile_verified_mark.svg" alt="verified" />
                        </div>
                      </div>
                      <div className={styles.guideDetails}>
                        <p className={styles.rating}>
                          <img src="images/star_icon.svg" alt="rating" /> {tour.rating>0 ? tour.rating : 'Unrated'}
                        </p>
                        <p>
                          <img src="images/holidays_icon.svg" alt="tours" /> {tour.undertaken_tours}{' '}
                          Tour{ tour.undertaken_tours > 1 ? 's' : ''} Booked

                        </p>
                        <p>
                          {tour.customer_created_date && (
                            <>
                              <img src="images/calendar_icon.svg" alt="since" />
                              {isNaN(new Date(tour.customer_created_date).getFullYear())
                                ? 'NA'
                                : `Since ${new Date(tour.customer_created_date).toLocaleString('en-US', {
                                  month: 'short',
                                  year: 'numeric',
                                })}`}
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className={styles.joinnowflex}>
                      <div className={styles.joinnowtitle}>
                        <h3 className={styles.title}>
                          Join {tour.display_name? tour.display_name : tour.customer_name} <br />
                          on a tour to {tour.latest_order_name}
                        </h3>
                        {/* <p className={styles.tourInfo}>
                          <strong>Tour Date:</strong>{' '}
                          {new Date(tour.latest_order_date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}{' '}
                          &nbsp; &nbsp;
                          <strong>Departing From:</strong> {tour.departing_from || 'N/A'}
                        </p> */}
                      </div>
                      <div>
                        <button
                          className={styles.joinBtn}
                          onClick={() =>
                            (window.location.href = `holiday/tourmasterdetail?id=${tour.customer_id}`)
                          }
                        >
                          Know More <img src="images/cta_arrow.svg" alt="join" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            </div>
          ))}
        </div>
      )}

      {!loading && tourCarouselData?.length > 1 && (
        <button
          className={`${styles.navBtn} ${styles.right}`}
          onClick={nextSlide}
          disabled={isTransitioning}
        >
          <img src="images/arrow_right.svg" alt="Next" />
        </button>
      )}
    </div>
  )
}